import { useState, useEffect, useCallback } from "react";
import "./App.css";
import axios, { API } from "@/utils/api";
import { Leaf, LogOut, Users, ChevronRight } from "lucide-react";
import { translations } from "@/i18n";
import { LangContext } from "@/contexts/LanguageContext";
import { AuthContext } from "@/contexts/AuthContext";
import LoginPage from "@/components/auth/LoginPage";
import RegisterPage from "@/components/auth/RegisterPage";
import WelcomePage from "@/components/auth/WelcomePage";
import ClientSelector from "@/components/ClientSelector";
import AdminPanel from "@/components/AdminPanel";
import ClientDashboard from "@/components/ClientDashboard";

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authView, setAuthView] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("co2e_lang") || "en");

  const changeLang = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem("co2e_lang", newLang);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
    setSelectedClient(null);
    setShowAdminPanel(false);
  };

  const fetchClients = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  }, [user]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      fetchClients();
    }
  }, [user, fetchClients]);

  const handleCreateClient = (newClient) => {
    setClients([...clients, newClient]);
  };

  if (!authChecked) {
    return (
      <div className="loading-container" style={{ height: "100vh" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <LangContext.Provider value={lang}>
      <AuthContext.Provider value={{ user, login, logout }}>
        <WelcomePage
          lang={lang}
          onChangeLang={changeLang}
          onSignIn={() => setAuthView("login")}
          onRegister={() => setAuthView("register")}
        />
        {authView === "login" && (
          <LoginPage
            onSwitchToRegister={() => setAuthView("register")}
            onClose={() => setAuthView(null)}
          />
        )}
        {authView === "register" && (
          <RegisterPage
            onSwitchToLogin={() => setAuthView("login")}
            onClose={() => setAuthView(null)}
          />
        )}
      </AuthContext.Provider>
      </LangContext.Provider>
    );
  }

  if (selectedClient && !showAdminPanel) {
    return (
      <LangContext.Provider value={lang}>
      <AuthContext.Provider value={{ user, login, logout }}>
        <ClientDashboard 
          client={selectedClient} 
          user={user}
          onBack={() => setSelectedClient(null)}
          lang={lang}
          onChangeLang={changeLang}
        />
      </AuthContext.Provider>
      </LangContext.Provider>
    );
  }

  return (
    <LangContext.Provider value={lang}>
    <AuthContext.Provider value={{ user, login, logout }}>
      <div className="app-container">
        <header className="top-bar">
          <div className="top-bar-logo">
            <div className="sidebar-logo-icon" style={{ width: "32px", height: "32px" }}>
              <Leaf size={18} />
            </div>
            <span className="sidebar-logo-text">CO2ePortal</span>
          </div>
          <div className="top-bar-user">
            <div className="lang-switcher" data-testid="lang-switcher">
              {["en","fr","es"].map(l => (
                <button key={l} className={`lang-btn ${lang===l?"active":""}`} onClick={()=>changeLang(l)} data-testid={`lang-${l}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <span className="user-name">{user.name}</span>
            <span className={`badge badge-${user.role === "admin" ? "success" : "info"}`}>{user.role}</span>
            {user.role === "admin" && (
              <button
                className={`btn ${showAdminPanel ? "btn-primary" : "btn-secondary"} btn-sm`}
                onClick={() => setShowAdminPanel(!showAdminPanel)}
                data-testid="toggle-admin-panel"
              >
                {showAdminPanel ? (
                  <>
                    <ChevronRight size={16} style={{ transform: "rotate(180deg)" }} />
                    {translations[lang]?.back_to_clients || "Back to Clients"}
                  </>
                ) : (
                  <>
                    <Users size={16} />
                    {translations[lang]?.admin_panel || "Admin Panel"}
                  </>
                )}
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={logout} data-testid="logout-btn">
              <LogOut size={16} />
              {translations[lang]?.logout || "Logout"}
            </button>
          </div>
        </header>

        <main className="main-content-full">
          {showAdminPanel ? (
            <AdminPanel />
          ) : (
            <ClientSelector
              clients={clients}
              selectedClient={selectedClient}
              onSelectClient={setSelectedClient}
              onCreateClient={handleCreateClient}
              user={user}
            />
          )}
        </main>
      </div>
    </AuthContext.Provider>
    </LangContext.Provider>
  );
}

export default App;
